import React from "react";

/**
 * Tactical risk gauge — semicircular SVG arc.
 * value: 0..1
 */
export const RiskGauge = ({ value = 0, size = 96, label = "Risk Index" }) => {
  const clamped = Math.max(0, Math.min(1, value));
  const angle = -90 + clamped * 180; // -90 (left) to +90 (right)
  const r = size * 0.4;
  const cx = size / 2;
  const cy = size * 0.62;
  const startAngle = -90;
  const endAngle = 90;

  const polar = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const arc = (a1, a2) => {
    const p1 = polar(a1);
    const p2 = polar(a2);
    const large = a2 - a1 > 180 ? 1 : 0;
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 1 ${p2.x} ${p2.y}`;
  };

  const tickColor = (t) =>
    t < 0.4 ? "hsl(var(--muted-foreground))" :
    t < 0.6 ? "hsl(var(--primary))" :
    t < 0.8 ? "hsl(var(--warning))" :
              "hsl(var(--destructive))";

  const status =
    clamped >= 0.8 ? { label: "CRITICAL", className: "sev-critical" } :
    clamped >= 0.6 ? { label: "HIGH",     className: "sev-high" } :
    clamped >= 0.4 ? { label: "ELEVATED", className: "sev-medium" } :
                     { label: "NOMINAL",  className: "sev-low" };

  const needle = polar(angle);

  return (
    <div className="flex items-center gap-3 min-w-0" data-testid="risk-gauge">
      <svg width={size} height={size * 0.62} viewBox={`0 0 ${size} ${size * 0.62}`} className="shrink-0">
        {/* base track */}
        <path d={arc(startAngle, endAngle)} fill="none" stroke="hsl(var(--border))" strokeWidth="5" />
        {/* segmented colored arcs */}
        {[
          [0, 0.4, "hsl(var(--muted-foreground))"],
          [0.4, 0.6, "hsl(var(--primary))"],
          [0.6, 0.8, "hsl(var(--warning))"],
          [0.8, 1.0, "hsl(var(--destructive))"],
        ].map(([a, b, col], i) => (
          <path
            key={i}
            d={arc(startAngle + a * 180, startAngle + b * 180)}
            fill="none"
            stroke={col}
            strokeWidth="5"
            opacity={clamped >= a ? 1 : 0.3}
          />
        ))}
        {/* needle */}
        <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke="hsl(var(--foreground))" strokeWidth="2" />
        <circle cx={cx} cy={cy} r="2.5" fill="hsl(var(--foreground))" />
      </svg>
      <div className="min-w-0">
        <div className="micro-label">{label}</div>
        <div className="font-mono text-2xl font-semibold tracking-tighter leading-none mt-0.5">
          {(clamped * 100).toFixed(0)}
        </div>
        <div className={`font-display font-black uppercase tracking-tight text-[11px] mt-0.5 ${status.className}`} data-testid="risk-gauge-status">
          {status.label}
        </div>
      </div>
    </div>
  );
};

export default RiskGauge;
